import { z } from 'zod/v4'
import { FloorRuleSetting } from './adm/rules'
import { BidderRule } from './bidder-rule'
import { HistorizedScopedModelSchema } from './base-models'
import { FormattedBidParamsSchema } from './bid-params-mapping'
import { KleanadsOutputSchema } from './kleanadsOutput'
import { MediaType, MediaTypeSchema } from './media-types'

export const PBSTCK_BIDDER_STACK_NAME = 'pbstck-bidder-stack'
export const SPARK_FOR_PUB_STACK_NAME = 'spark-for-pub-stack'

export const STACK_TYPES = ['pbs', 'adm', 'pbstck-bidder', 'spark-for-pub'] as const
export const STACK_STATUSES = ['to-deploy', 'live', 'archived', 'archived_group'] as const

// Zod Schemas

export const StackTypeSchema = z.enum(STACK_TYPES)
export const StackStatusSchema = z.enum(STACK_STATUSES)

export const StackFloorRuleConfigEntitySchema = z.object({
  floorRuleId: z.string(),
  adUnitIds: z.array(z.string()),
})

const StackFloorRuleConfigSchema = z
  .object({
    standardFloorRuleId: z.string(),
    // Stored as null when no specific rule is selected, so accept null in addition to undefined.
    specificFloorRuleConfigs: z.array(StackFloorRuleConfigEntitySchema).optional().nullable(),
  })
  .optional()

const RuleConfigSchema = z.object({
  // Stored as null when no rule is selected for a device, so accept null in addition to undefined.
  ruleId: z.string().nullish(),
  device: z.string(),
})

const RuleConfigWithAdUnitSchema = RuleConfigSchema.extend({
  adUnitId: z.string(),
})

export const PbsStackConfigurationSchema = z.object({
  contextId: z.string().or(z.null()),
  bidderRuleId: z.string(),
  floorRuleConfig: StackFloorRuleConfigSchema.nullable(),
})

export const AdmStackConfigurationSchema = PbsStackConfigurationSchema.extend({
  mobileFloorRuleConfig: StackFloorRuleConfigSchema.nullable(),
  headerBiddingEnabled: z.boolean(),
  adCallTimeout: z.number().optional(),
  refreshRuleConfig: z.array(RuleConfigWithAdUnitSchema).optional().nullable(),
  lazyLoadingRuleConfig: z.array(RuleConfigSchema).optional().nullable(),
  desktopAdUnitsIds: z.array(z.string()),
  mobileAdUnitsIds: z.array(z.string()),
  desktopRoadblockAdUnitsIds: z.array(z.string()).optional().nullable(),
  mobileRoadblockAdUnitsIds: z.array(z.string()).optional().nullable(),
})

export const StackConfigurationSchema = z.union([PbsStackConfigurationSchema, AdmStackConfigurationSchema])

export const PbsStackMappingElementOutputSchema = z.object({
  bidderName: z.string(),
  mediaTypes: z.array(MediaTypeSchema),
  params: FormattedBidParamsSchema,
})

export const PbsStackMappingOutputSchema = z.record(z.string(), z.array(PbsStackMappingElementOutputSchema))

export const SpecificFloorSchema = z.object({
  dimensions: z
    .object({
      mediaTypes: z.array(MediaTypeSchema),
      sizes: z.array(z.string()),
    })
    .optional(),
  bidfloor: z.number(),
})

export const StackFloorRuleSchema = z.object({
  // A rule with no floor price: stored as `null` by older builds, left out by the canonical build. Both mean no floor.
  bidfloor: z.number().nullish(),
  specificFloors: z.array(SpecificFloorSchema),
})
export const FloorsOutputSchema = z.object({
  currency: z.string(),
  defaultRule: StackFloorRuleSchema,
  rulesByAdUnit: z.record(z.string(), StackFloorRuleSchema),
})
export const PbsStackOutputSchema = z.object({
  adUnits: PbsStackMappingOutputSchema,
  placements: PbsStackMappingOutputSchema.optional(),
  bidderAliases: z.record(z.string(), z.string()),
  bidAdjustments: z.record(z.string(), z.number()),
  floors: FloorsOutputSchema.optional(),
  product: z.enum(['node', 'one']).optional(),
  siteEnv: z.enum(['web', 'ios', 'android', 'amp']).optional(),
  adUnitTypes: z.record(z.string(), z.enum(['banner', 'instream'])).optional(),
})

export const ConditionElementSchema = z.object({
  op: z.enum(['IN', 'NOT_IN']),
  key: z.string(),
  values: z.array(z.string()),
})

export const ConditionsGroupSchema = z.object({
  op: z.enum(['AND', 'OR']),
  get elements() {
    return z.array(z.union([ConditionsGroupSchema, ConditionElementSchema]))
  },
})

/**
 * A One stack's output: one kleanads config per device, and the server-side variant of ADR-0036.
 *
 * `serverside` is a Prebid Server output, shaped like a Node stack's: prebid-server-proxy reads it out of the
 * database, and it never goes on the CDN. It is absent when the site maps none of the server-side seats the
 * stack's bidder rule selects.
 */
export const AdmStackOutputSchema = z.object({
  desktop: KleanadsOutputSchema,
  mobile: KleanadsOutputSchema,
  serverside: PbsStackOutputSchema.optional(),
})

export const StackOutputSchema = z.union([PbsStackOutputSchema, AdmStackOutputSchema])

export const StackBaseSchema = HistorizedScopedModelSchema.extend({
  stackGroupId: z.uuid(),
  type: StackTypeSchema,
  name: z.string(),
  configuration: StackConfigurationSchema,
  version: z.int32(),
  status: StackStatusSchema,
  ratio: z.number(),
  // The Default stack (and archived stacks) have no priority, stored as null — accept null in addition to undefined.
  priority: z.number().nullish(),
  snapshot: StackOutputSchema,
  output: StackOutputSchema.optional(),
  isOutOfSync: z.boolean().default(false),
  snapshotHash: z.string().optional(),
  commitMessage: z.string().optional(),
  siteId: z.uuid().optional(),
})

export const PbsStackSchema = StackBaseSchema.extend({
  type: z.literal('pbs'),
  siteId: z.uuid(),
  snapshot: PbsStackOutputSchema,
  output: PbsStackOutputSchema.optional(),
  configuration: PbsStackConfigurationSchema,
})
/**
 * A stack the build itself ships: no deployment step promotes it, so the write that rebuilds it is the
 * write that serves it. True on the two scope-level types, which have no deployment of their own. Absent on
 * documents written before the field existed — read it through `isDeliveredAsBuilt`, never on its own.
 */
export const PbstckBidderStackSchema = StackBaseSchema.extend({
  type: z.literal('pbstck-bidder'),
  snapshot: PbsStackOutputSchema,
  output: PbsStackOutputSchema.optional(),
  configuration: PbsStackConfigurationSchema,
  autoDeploy: z.boolean().optional(),
})
export const SparkForPubStackOutputSchema = PbsStackOutputSchema
export const SparkForPubStackSchema = StackBaseSchema.extend({
  type: z.literal('spark-for-pub'),
  snapshot: PbsStackOutputSchema,
  output: PbsStackOutputSchema.optional(),
  configuration: PbsStackConfigurationSchema,
  autoDeploy: z.boolean().optional(),
})
export const AdmStackSchema = StackBaseSchema.extend({
  type: z.literal('adm'),
  siteId: z.uuid(),
  snapshot: AdmStackOutputSchema,
  output: AdmStackOutputSchema.optional(),
  configuration: AdmStackConfigurationSchema,
  conditions: ConditionsGroupSchema.optional(),
})

export const CreateStackBaseSchema = StackBaseSchema.omit({
  id: true,
  scopeId: true,
  updatedAt: true,
  updatedBy: true,
  version: true,
  status: true,
  ratio: true,
  snapshot: true,
  output: true,
  isOutOfSync: true,
}).extend({
  // The Default stack has no priority, stored as null — accept null in addition to undefined.
  priority: z.number().nullish(),
  stackGroupId: z.uuid().optional(),
})

const CreatePbsStackSchema = CreateStackBaseSchema.extend({
  type: z.literal('pbs'),
  siteId: z.uuid(),
  configuration: PbsStackConfigurationSchema,
})

const CreateAdmStackSchema = CreateStackBaseSchema.extend({
  type: z.literal('adm'),
  siteId: z.uuid(),
  configuration: AdmStackConfigurationSchema,
})

// Inferred Types
export type StackType = z.infer<typeof StackTypeSchema>
export type StackStatus = z.infer<typeof StackStatusSchema>
export type FormattedBidParams = z.infer<typeof FormattedBidParamsSchema>
export type PbsStackMappingOutput = z.infer<typeof PbsStackMappingOutputSchema>
export type AdmStackOutput = z.infer<typeof AdmStackOutputSchema>
export type PbsStackOutput = z.infer<typeof PbsStackOutputSchema>
export type StackConfiguration = z.infer<typeof StackConfigurationSchema>
export type PbsStackConfiguration = z.infer<typeof PbsStackConfigurationSchema>
export type AdmStackConfiguration = z.infer<typeof AdmStackConfigurationSchema>
export type StackOutput = z.infer<typeof StackOutputSchema>
export type CreateAdmStack = z.infer<typeof CreateAdmStackSchema>
export type PbsStack = z.infer<typeof PbsStackSchema>
export type AdmStack = z.infer<typeof AdmStackSchema>
export type PbstckBidderStack = z.infer<typeof PbstckBidderStackSchema>
export type PbstckBidderStackOutput = z.infer<typeof PbsStackOutputSchema>
export type SparkForPubStackOutput = z.infer<typeof SparkForPubStackOutputSchema>
export type SparkForPubStack = z.infer<typeof SparkForPubStackSchema>
export const StackSchema = z.discriminatedUnion('type', [PbsStackSchema, AdmStackSchema, PbstckBidderStackSchema, SparkForPubStackSchema])
export const CreateStackSchema = z.discriminatedUnion('type', [CreatePbsStackSchema, CreateAdmStackSchema])
export type Stack = z.infer<typeof StackSchema>
/** One entry of a site deployment history, without `snapshot`/`output`. `id` addresses the full revision. */
export type LightStackHistory = Pick<Stack, 'id' | 'scopeId' | 'siteId' | 'stackGroupId' | 'type' | 'name' | 'version' | 'status' | 'ratio' | 'updatedAt' | 'updatedBy'>
export type CreateStack = z.infer<typeof CreateStackSchema>
export type CreateSiteLevelStack = CreateStack
export type FloorsOutput = z.infer<typeof FloorsOutputSchema>
export type SpecificFloor = z.infer<typeof SpecificFloorSchema>
export type FloorRuleConfig = z.infer<typeof StackFloorRuleConfigSchema>

// Type guards using Zod schemas
export const isPbsStack = (stack: Stack): stack is PbsStack => stack.type === 'pbs'
export const isAdmStack = (stack: Stack): stack is AdmStack => stack.type === 'adm'
export const isPbstckBidderStack = (stack: Stack): stack is PbstckBidderStack => stack.type === 'pbstck-bidder'
export const isSparkForPubStack = (stack: Stack): stack is SparkForPubStack => stack.type === 'spark-for-pub'

export const SCOPE_LEVEL_STACK_TYPES = ['pbstck-bidder', 'spark-for-pub'] as const satisfies readonly StackType[]
export type ScopeLevelStackType = (typeof SCOPE_LEVEL_STACK_TYPES)[number]
export const isScopeLevelStackType = (type: StackType): type is ScopeLevelStackType => (SCOPE_LEVEL_STACK_TYPES as readonly StackType[]).includes(type)

export const BulkUpdateStackSchema = z.object({
  id: z.string(),
  status: StackStatusSchema,
  ratio: z.number(),
  updatedAt: z.coerce.date(),
  // null clears the priority (e.g. the Default stack / archived stacks have none).
  priority: z.number().nullish(),
})
export type BulkUpdateStack = z.infer<typeof BulkUpdateStackSchema>

// Additional validation functions
export const validateStack = (data: unknown): Stack => StackSchema.parse(data)
export const validateCreateStack = (data: unknown): CreateStack => CreateStackSchema.parse(data)
export const validatePbsStack = (data: unknown): PbsStack => PbsStackSchema.parse(data)
export const validateKleanadsStack = (data: unknown): AdmStack => AdmStackSchema.parse(data)
export const validateBulkUpdateStack = (data: unknown): BulkUpdateStack => BulkUpdateStackSchema.parse(data)

export interface BulkUpdateStacksResult {
  successful: string[]
  failed: Array<{ stackId: string; error: string }>
}

export type LazyLoadingRuleConfig = {
  device: string
  ruleId: string
}

export type AdUnitConfig = {
  adUnitId: string
  roadblock?: RoadblockType
}

export enum RoadblockType {
  Master = 'master',
  Companion = 'companion',
}

export type AdunitDeviceRuleConfig = {
  device: string
  ruleId: string
  adUnitIds: string[]
}

export type StacksBySite = {
  [siteName: string]: {
    /** The media's id: the key is its name. */
    siteId: string
    stackId: string
    stackName: string
    version: string
    enabled: boolean
    /** Which product deploys the stack. A rule is shared, so one rule's usage spans both. */
    type: Extract<StackType, 'adm' | 'pbs'>
    /** Only to-deploy and live stacks are counted as a use. */
    status: Extract<StackStatus, 'to-deploy' | 'live'>
    /** The share of the traffic a live version serves, or would be deployed at for a to-deploy one. */
    ratio: number
    isOutOfSync: boolean
    /** No newer version of the same stack is to deploy or live — the one a change is made on. */
    isLatestVersion: boolean
  }[]
}

export type SiteStacksByProperty = {
  [propertyId: string]: StacksBySite
}

/**
 * How many stacks a rule (or context, or ad unit) is used by, across every media.
 *
 * `StacksBySite` is keyed by media and holds a list per media, so "is it used at all?" is not
 * `Object.keys(...).length` — a media key with an empty list would read as a use. Lives here rather
 * than in the app because the archive guard on the server and the "N stacks" label in the browser
 * have to agree on what counts.
 */
export const countStacksUse = (stacksUse?: StacksBySite): number => (stacksUse ? Object.values(stacksUse).reduce((sum, stacks) => sum + stacks.length, 0) : 0)

const RESERVED_STACK_NAME = 'default'

export const isStackNameReserved = (name: string): boolean => {
  return RESERVED_STACK_NAME === name.toLowerCase().trim()
}

export type DisplayedBidder = {
  bidderName: string
  desktop: string[]
  mobile: string[]
  isServerSide: boolean
  bidderSeatId: string
}

export type DisplayedRule = {
  ruleName: string
  adUnitNames: string[]
}

export type DisplayedFloorRule = DisplayedRule & {
  floor: string
  settings?: FloorRuleSetting[]
  dynamic?: boolean
}

export type DisplayedRefreshRule = DisplayedRule

/**
 * A bidder rule as the stack views show it, alongside `DisplayedFloorRule` and `DisplayedRefreshRule`.
 *
 * Seat names rather than the rule's own seat ids: the views that render this have the seat list to
 * hand and a reader needs the name, so the join happens once at the call site instead of in every
 * component that displays a rule.
 */
export type DisplayedHeaderBiddingRule = {
  ruleName: string
  autoAddNewBidderSeats: BidderRule['autoAddNewBidderSeats']
  bidders: { name: string; mediaTypes: MediaType[] }[]
}

export const isLiveOutOfSync = (stack: { status: StackStatus; isOutOfSync?: boolean | null }): boolean => stack.status === 'live' && stack.isOutOfSync === true
